import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Strip raw markdown asterisks (e.g., **bold**) that leak through from LLM
// responses. Applied recursively to every string in API + SSE payloads so the
// user never sees a literal `*` anywhere on the site.
export function stripStars(value) {
  if (typeof value === "string") return value.replace(/\*/g, "");
  if (Array.isArray(value)) return value.map(stripStars);
  if (value && typeof value === "object") {
    const out = {};
    for (const k of Object.keys(value)) out[k] = stripStars(value[k]);
    return out;
  }
  return value;
}

export const api = axios.create({ baseURL: API, timeout: 60000 });

// Cold-start shield — production scales to zero, so the FIRST request after
// idle can hit a waking backend and fail (network error / 5xx / Cloudflare
// 520-524). GETs are idempotent, so transparently retry them with backoff
// instead of surfacing a broken page. Applies site-wide to every module.
const RETRYABLE_STATUS = new Set([500, 502, 503, 504, 520, 521, 522, 523, 524]);
const RETRY_DELAYS_MS = [1500, 4000, 8000, 15000];
api.interceptors.response.use(undefined, async (error) => {
  const cfg = error?.config;
  if (!cfg || (cfg.method || "").toLowerCase() !== "get") return Promise.reject(error);
  // Offline → fail fast so bundled-seed fallbacks kick in instantly.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return Promise.reject(error);
  const status = error?.response?.status;
  if (error.response && !RETRYABLE_STATUS.has(status)) return Promise.reject(error);
  cfg.__retryCount = (cfg.__retryCount || 0) + 1;
  if (cfg.__retryCount > RETRY_DELAYS_MS.length) return Promise.reject(error);
  await new Promise((res) => setTimeout(res, RETRY_DELAYS_MS[cfg.__retryCount - 1]));
  return api.request(cfg);
});

// Phase B auth — auto-attach the magic-link JWT to every backend
// request so RBAC-gated endpoints (/api/auth/me, /api/institutional-reports/*,
// /api/assessments) see the caller. Existing legacy `maldek_token`
// systems remain untouched.
api.interceptors.request.use((cfg) => {
  try {
    const jwt = localStorage.getItem("maldek_jwt");
    if (jwt && !cfg.headers?.Authorization) {
      cfg.headers = cfg.headers || {};
      cfg.headers.Authorization = `Bearer ${jwt}`;
    }
  } catch { /* silent */ }
  return cfg;
});

// Global response interceptor — strip `*` from every JSON payload, except
// Street History (intentional **bold** markdown rendered as <strong>) and
// /synthesize (pathophys + others rely on **bold**/*italic*/blockquotes
// to render highlighted, scannable result cards — the markdown chars
// MUST reach ReactMarkdown intact).
api.interceptors.response.use((r) => {
  const url = r?.config?.url || "";
  if (url.startsWith("/street-history")) return r;
  if (url.startsWith("/synthesize")) return r;
  if (r && r.data) r.data = stripStars(r.data);
  return r;
});

// ---------------------------------------------------------------------------
// Client-side GET cache — the single biggest perceived-speed win. Content
// endpoints are deterministic and change rarely, but every page re-fetched
// them on each mount, adding a full network round-trip per navigation.
// We transparently cache GETs to those content endpoints (in-memory +
// sessionStorage) so repeat visits and back/forward navigation are instant.
// User-specific / dynamic endpoints (auth, progress, analytics, live
// session state, translation) are NEVER cached.
// ---------------------------------------------------------------------------
const GET_CACHE_TTL_MS = 15 * 60 * 1000; // 15 min
const GET_CACHE_MAX_BYTES = 700 * 1024;  // don't persist huge blobs to sessionStorage
const _getCache = new Map(); // key -> { resp, ts }

const CACHEABLE_GET_PREFIXES = [
  "/pathways", "/pediatrics", "/catalog", "/clinical-reasoning/releases",
  "/med-library", "/medications", "/street-history", "/symptoms",
  "/lab-reference", "/labs", "/imaging", "/therapeutics", "/cooking",
  "/pathophysiology", "/pathophys", "/explain", "/nclex-practice/facets",
  "/encounter/seed-patients", "/encounter/interview/", "/encounter/checkpoint/",
  "/stressors", "/sdoh/catalog", "/health/modules",
];
// Never cache these (dynamic / per-user / live) even if a prefix matches.
const NONCACHEABLE_GET_SUBSTRINGS = [
  "/analytics", "/me/", "/auth", "/access", "/runs", "/progress",
  "/cohort", "/surveys", "/translate", "/encounter/start",
  "/nclex-practice/progress", "/nclex-practice/attempt",
];

function _isCacheableGet(url, config) {
  if (typeof url !== "string") return false;
  if (config && (config.responseType || (config.headers && config.headers["X-Admin-Key"]))) return false;
  const path = url.split("?")[0];
  if (NONCACHEABLE_GET_SUBSTRINGS.some((s) => url.includes(s))) return false;
  return CACHEABLE_GET_PREFIXES.some((p) => path.startsWith(p));
}

function _cacheKey(url, config) {
  const params = config && config.params ? JSON.stringify(config.params) : "";
  return url + params;
}

function _readSession(key) {
  try {
    const raw = localStorage.getItem("mgc:" + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > GET_CACHE_TTL_MS) return null;
    return parsed;
  } catch { return null; }
}

function _writeSession(key, data) {
  const s = JSON.stringify({ data, ts: Date.now() });
  if (s.length > GET_CACHE_MAX_BYTES) return;
  try {
    localStorage.setItem("mgc:" + key, s);
  } catch {
    // quota full — evict all cached payloads and retry once
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("mgc:"))
        .forEach((k) => localStorage.removeItem(k));
      localStorage.setItem("mgc:" + key, s);
    } catch { /* storage disabled — silent */ }
  }
}

const _origGet = api.get.bind(api);
const _inflight = new Map(); // key -> Promise (dedupe concurrent identical GETs)
api.get = function cachedGet(url, config) {
  if (!_isCacheableGet(url, config)) return _origGet(url, config);
  const key = _cacheKey(url, config);
  const now = Date.now();

  const mem = _getCache.get(key);
  if (mem && now - mem.ts < GET_CACHE_TTL_MS) return Promise.resolve(mem.resp);

  const sess = _readSession(key);
  if (sess) {
    const resp = { data: sess.data, status: 200, statusText: "OK (cache)", headers: {}, config: { url }, cached: true };
    _getCache.set(key, { resp, ts: sess.ts });
    return Promise.resolve(resp);
  }

  // Coalesce concurrent identical requests into one network round-trip.
  const pending = _inflight.get(key);
  if (pending) return pending;

  const p = _origGet(url, config).then((resp) => {
    _getCache.set(key, { resp, ts: now });
    _writeSession(key, resp.data);
    _inflight.delete(key);
    return resp;
  }).catch((err) => {
    _inflight.delete(key);
    throw err;
  });
  _inflight.set(key, p);
  return p;
};

export function clearGetCache() {
  _getCache.clear();
  try {
    Object.keys(sessionStorage).forEach((k) => { if (k.startsWith("mgc:")) sessionStorage.removeItem(k); });
  } catch { /* silent */ }
}

// Warm the cache for the hottest content endpoints during idle time after
// first paint, so the user's first click into any module is instant.
export function warmTopEndpoints() {
  const run = () => {
    const targets = [
      "/pathways/summary",
      "/pathways/canonical/titles",
      "/pathways?limit=400",
      "/pediatrics/overview",
      "/pediatrics/conditions",
      "/pediatrics/categories",
      "/street-history/v3/catalog/by-system",
      "/med-library/systems",
      "/med-library/stats",
      "/nclex-practice/facets",
      "/encounter/seed-patients",
    ];
    targets.forEach((u) => { api.get(u).catch(() => {}); });
  };
  try {
    if (typeof window !== "undefined" && window.requestIdleCallback) {
      window.requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 1200);
    }
  } catch { setTimeout(run, 1200); }
}


export const fetchStressors = () => api.get("/stressors").then((r) => r.data);
export const fetchPathoBridge = (stressor_id, level = "student", provider = "claude") =>
  api.post("/pathobridge", { stressor_id, level, provider }).then((r) => r.data);
export const explainTopic = (payload) => api.post("/explain", payload).then((r) => r.data);
export const fetchPatients = () => api.get("/patients").then((r) => r.data);
export const patientChat = (payload) => api.post("/patient/chat", payload).then((r) => r.data);
export const patientHistory = (sid) =>
  api.get(`/patient/history/${sid}`).then((r) => r.data);
export const preceptorChallenge = (payload) =>
  api.post("/preceptor/challenge", payload).then((r) => r.data);
export const preceptorGrade = (payload) =>
  api.post("/preceptor/grade", payload).then((r) => r.data);
export const simStart = (payload) => api.post("/simulator/start", payload).then((r) => r.data);
export const simGet = (sim_id) => api.get(`/simulator/${sim_id}`).then((r) => r.data);
export const simStep = (payload) => api.post("/simulator/step", payload).then((r) => r.data);
export const speak = (text) => api.post("/tts/speak", { text }).then((r) => r.data);
export const reversePathway = (disease) =>
  api.post("/reverse-pathway", { disease }).then((r) => r.data);

// Whisper STT — multipart upload of recorded audio
export async function transcribeAudio(blob, filename = "clip.webm", language = "en") {
  const fd = new FormData();
  fd.append("audio", blob, filename);
  if (language) fd.append("language", language);
  const r = await api.post("/stt/transcribe", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 90000,
  });
  return r.data; // { text }
}

// Auth-protected
export const listSimRuns = () => api.get("/runs/simulations").then((r) => r.data);
export const listPatientSessions = () => api.get("/runs/patient-sessions").then((r) => r.data);
export const getProgress = () => api.get("/me/progress").then((r) => r.data);
export const cohortStart = (payload) => api.post("/cohort/compare/start", payload).then((r) => r.data);

// SDoH catalog + analyzer
export const fetchSdohCatalog = () => api.get("/sdoh/catalog").then((r) => r.data);
export const analyzeSdoh = (item_id, provider = "claude") =>
  api.post("/sdoh/analyze", { item_id, provider }).then((r) => r.data);

// Medication + Pathophysiology Breakdown
export const fetchPopularMedications = () =>
  api.get("/medication/popular").then((r) => r.data);
export const fetchMedicationBreakdown = (payload) =>
  api.post("/medication/breakdown", payload).then((r) => r.data);

// Cohort (instructor / classroom)
export const listMyCohorts = () => api.get("/cohort/mine").then((r) => r.data);
export const createCohort = (payload) => api.post("/cohort/create", payload).then((r) => r.data);
export const joinCohort = (code) => api.post("/cohort/join", { code }).then((r) => r.data);
export const getCohort = (id) => api.get(`/cohort/${id}`).then((r) => r.data);
export const getCohortAnalytics = (id) => api.get(`/cohort/${id}/analytics`).then((r) => r.data);
export const getCohortMembers = (id) => api.get(`/cohort/${id}/members`).then((r) => r.data);
export const getMemberActivity = (cid, uid) =>
  api.get(`/cohort/${cid}/members/${uid}/activity`).then((r) => r.data);

// Assignments
export const listAssignments = (cid) =>
  api.get(`/cohort/${cid}/assignments`).then((r) => r.data);
export const createAssignment = (cid, payload) =>
  api.post(`/cohort/${cid}/assignments`, payload).then((r) => r.data);
export const getAssignment = (cid, aid) =>
  api.get(`/cohort/${cid}/assignments/${aid}`).then((r) => r.data);
export const deleteAssignment = (cid, aid) =>
  api.delete(`/cohort/${cid}/assignments/${aid}`).then((r) => r.data);
export const submitReflection = (cid, aid, payload) =>
  api.post(`/cohort/${cid}/assignments/${aid}/submit`, payload).then((r) => r.data);
export const listSubmissions = (cid, aid) =>
  api.get(`/cohort/${cid}/assignments/${aid}/submissions`).then((r) => r.data);

// Export download helpers (return browser-downloadable URL with token)
export const exportUrl = (path) => {
  const token = localStorage.getItem("maldek_token");
  const sep = path.includes("?") ? "&" : "?";
  return `${API}${path}${sep}_t=${encodeURIComponent(token || "")}`;
};
export async function downloadFile(path, filename) {
  const r = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Surveys — pre/post effectiveness measurement
// ---------------------------------------------------------------------------
export const submitSurvey = (payload) =>
  api.post("/surveys/submit", payload).then((r) => r.data);
export const fetchMySurveys = (participant_id, audience) =>
  api.get(`/surveys/me?participant_id=${encodeURIComponent(participant_id)}&audience=${audience}`).then((r) => r.data);
export const fetchSurveyAggregate = (audience) =>
  api.get(`/surveys/aggregate${audience ? `?audience=${audience}` : ""}`).then((r) => r.data);

